import {
	Badge,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import {
	WEBHOOK_EVENT_DETAILS,
	type WebhookDeliveryDto,
	type WebhookDeliveryList,
} from "@dune/types";
import { Eye } from "lucide-react";
import { WEBHOOK_STATUS_LABELS, getStatusBadgeVariant } from "../utils";
import { formatDateTime } from "@dune/utils";

export interface WebhookDeliveriesTabProps {
	deliveries: WebhookDeliveryDto[];
	meta?: WebhookDeliveryList["meta"];
	isLoading: boolean;
	isRefetching: boolean;
	error: unknown;
	onPageChange: (page: number) => void;
	onInspect: (delivery: WebhookDeliveryDto) => void;
}

export const WebhookDeliveriesTab = ({
	deliveries,
	meta,
	isLoading,
	isRefetching,
	error,
	onPageChange,
	onInspect,
}: WebhookDeliveriesTabProps) => {
	const handleRowClick = (delivery: WebhookDeliveryDto) => {
		onInspect(delivery);
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className="space-y-3">
					{Array.from({ length: 6 }).map((index) => (
						<Skeleton
							key={`delivery-skeleton-${index}`}
							className="h-12 w-full"
						/>
					))}
				</div>
			);
		}

		if (error) {
			return (
				<div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-destructive">
					Impossible de charger l'historique des évènements.
				</div>
			);
		}

		if (deliveries.length === 0) {
			return (
				<div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed p-8 text-center">
					<p className="text-muted-foreground">
						Aucun évènement n'a encore ete diffuse.
					</p>
				</div>
			);
		}

		return (
			<>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Nom du webhook</TableHead>
							<TableHead>Lien du webhook</TableHead>
							<TableHead>Évènement</TableHead>
							<TableHead>Statut</TableHead>
							<TableHead>Dernier envoi</TableHead>
							<TableHead className="w-[80px] text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{deliveries.map((delivery) => (
							<TableRow
								key={delivery.id}
								onClick={() => handleRowClick(delivery)}
								className="cursor-pointer hover:bg-muted/50"
							>
								<TableCell>
									<div className="font-medium">
										{delivery.webhook?.name ?? `Webhook #${delivery.webhookId}`}
									</div>
								</TableCell>
								<TableCell>
									{delivery.webhook?.url && (
										<a
											href={delivery.webhook.url}
											target="_blank"
											rel="noreferrer"
											className="text-xs text-primary underline break-all"
											onClick={(e) => e.stopPropagation()}
										>
											{delivery.webhook.url}
										</a>
									)}
									{!delivery.webhook?.url && (
										<span className="text-xs text-muted-foreground">—</span>
									)}
								</TableCell>
								<TableCell>
									<Badge variant="outline">
										{WEBHOOK_EVENT_DETAILS[delivery.event]?.label ??
											delivery.event}
									</Badge>
								</TableCell>
								<TableCell>
									<Badge variant={getStatusBadgeVariant(delivery.status)}>
										{WEBHOOK_STATUS_LABELS[delivery.status] ?? delivery.status}
									</Badge>
								</TableCell>
								<TableCell>
									{delivery.lastAttemptAt
										? formatDateTime(delivery.lastAttemptAt)
										: "—"}
								</TableCell>
								<TableCell className="text-right">
									<Button
										variant="ghost"
										size="icon"
										onClick={(e) => {
											e.stopPropagation();
											handleRowClick(delivery);
										}}
										aria-label="Voir les details de la diffusion"
									>
										<Eye className="h-4 w-4" />
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				{meta && (
					<div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
						<span>
							Page {meta.page} / {meta.totalPages} · {meta.total} évènement
							{meta.total > 1 ? "s" : ""}
						</span>
						<div className="flex gap-2">
							<Button
								variant="outline"
								onClick={() => onPageChange(Math.max(1, meta.page - 1))}
								disabled={!meta.hasPreviousPage || isRefetching}
							>
								Precedent
							</Button>
							<Button
								variant="outline"
								onClick={() => onPageChange(meta.page + 1)}
								disabled={!meta.hasNextPage || isRefetching}
							>
								Suivant
							</Button>
						</div>
					</div>
				)}
			</>
		);
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>Évènements envoyes</CardTitle>
				<CardDescription>
					Historique global des appels envoyes aux webhooks actifs.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{isRefetching && !isLoading && deliveries.length > 0 && (
					<p className="mb-2 text-xs text-muted-foreground">
						Actualisation des donnees...
					</p>
				)}
				{renderContent()}
			</CardContent>
		</Card>
	);
};
